// Phase 6 — shared MapLibre dark map style + helpers.
//
// We use CARTO's free "Dark Matter" VECTOR GL style (no API key). Vector lets
// us recolor roads (white) and buildings (grey) on a pure-black background for
// a crisp, legible cinematic look — something a flat raster basemap can't do.
export const DARK_MAP_STYLE = "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";

export const DEFAULT_MAP_VIEW = {
  center: [28.9784, 41.0082], // [lng, lat] — Istanbul as a neutral default
  zoom: 13,
  pitch: 0,
  bearing: 0,
};

export const LANDMARK_CATEGORIES = [
  "project", "school", "hospital", "shopping", "transport",
  "beach", "park", "airport", "restaurant", "landmark",
];

export const LANDMARK_SIZES = ["small", "medium", "large"];

// Build the DOM element for a landmark's MapLibre marker. Supports 3 sizes
// (small/medium/large per importance) and an optional PNG logo (icon_url) —
// used e.g. for the project's own logo pin.
export function createPinElement(lm, { pulse = false, sublabel = "" } = {}) {
  const sz = lm.size === "large" ? "lg" : lm.size === "small" ? "sm" : "md";
  const el = document.createElement("div");
  el.className = `map-pin map-pin-${sz}${pulse ? " map-pin-viewer" : ""}`;
  el.style.setProperty("--pin", lm.color || "#C4A47C");
  const label = escapeHtml(lm.name || "");
  const sub = sublabel ? `<span class="map-pin-sub">${escapeHtml(sublabel)}</span>` : "";
  const glyph = lm.icon_url
    ? `<span class="map-pin-logo"><img src="${escapeAttr(lm.icon_url)}" alt="" /></span>`
    : `<span class="map-pin-dot"></span>`;
  if (lm.icon_url) el.classList.add("map-pin-haslogo");
  el.innerHTML = `${glyph}<span class="map-pin-label">${label}${sub}</span>`;
  return el;
}
function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m])); }
function escapeAttr(s) { return String(s).replace(/"/g, "&quot;"); }

// Recolor the vector dark-matter layers → white roads, grey buildings, black
// background, so the map reads clearly. Safe to call after map 'load'.
export function applyDarkTheme(map) {
  try {
    const layers = (map.getStyle() && map.getStyle().layers) || [];
    layers.forEach((l) => {
      const sl = l["source-layer"];
      try {
        if (l.type === "background") {
          map.setPaintProperty(l.id, "background-color", "#000000");
        } else if (l.type === "line" && sl === "transportation") {
          map.setPaintProperty(l.id, "line-color", "#f2f2f2");
        } else if (l.type === "fill" && sl === "building") {
          map.setPaintProperty(l.id, "fill-color", "#6b6b70");
          map.setPaintProperty(l.id, "fill-opacity", 0.7);
        } else if (l.type === "fill" && sl === "water") {
          map.setPaintProperty(l.id, "fill-color", "#12233a");
        } else if (l.type === "symbol") {
          map.setPaintProperty(l.id, "text-color", "#ffffff");
          map.setPaintProperty(l.id, "text-halo-color", "#000000");
        }
      } catch (_) { /* layer may not support the prop */ }
    });
  } catch (_) { /* style not ready */ }
}

export function emptyLocationMap() {
  return {
    enabled: false,
    center: DEFAULT_MAP_VIEW.center,
    zoom: DEFAULT_MAP_VIEW.zoom,
    pitch: DEFAULT_MAP_VIEW.pitch,
    bearing: DEFAULT_MAP_VIEW.bearing,
    landmarks: [],
    routes: [],
  };
}

export function normalizeLocationMap(lm) {
  const base = emptyLocationMap();
  if (!lm || typeof lm !== "object") return base;
  return {
    ...base,
    ...lm,
    center: Array.isArray(lm.center) && lm.center.length === 2 ? lm.center : base.center,
    landmarks: Array.isArray(lm.landmarks) ? lm.landmarks : [],
    routes: Array.isArray(lm.routes) ? lm.routes : [],
  };
}

// Haversine distance (metres) between two [lng, lat] points.
export function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const la1 = toRad(a[1]);
  const la2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Total length of a polyline (array of [lng, lat]) in metres.
export function polylineMeters(coords) {
  let total = 0;
  for (let i = 1; i < (coords || []).length; i++) total += haversineMeters(coords[i - 1], coords[i]);
  return total;
}

export function formatDistance(m) {
  if (!m || m < 1) return "0 m";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;
}

// Point at the geometric middle (by length) of a polyline — for placing a
// distance label on the route.
export function midpointOfPolyline(coords) {
  if (!coords || coords.length === 0) return null;
  if (coords.length === 1) return coords[0];
  const total = polylineMeters(coords);
  let target = total / 2;
  for (let i = 1; i < coords.length; i++) {
    const seg = haversineMeters(coords[i - 1], coords[i]);
    if (seg >= target) {
      const k = seg === 0 ? 0 : target / seg;
      const a = coords[i - 1], b = coords[i];
      return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
    }
    target -= seg;
  }
  return coords[coords.length - 1];
}


// The "project" origin: the landmark flagged category==='project' (else the
// map center). Used for per-landmark "distance from project" labels.
export function projectOrigin(cfg) {
  const p = (cfg.landmarks || []).find((l) => l.category === "project");
  if (p) return [p.lng, p.lat];
  return cfg.center;
}

// Bounding box [[minLng,minLat],[maxLng,maxLat]] covering all landmarks +
// route coords, or null when there's nothing to frame.
export function boundsOf(cfg) {
  const pts = [];
  (cfg.landmarks || []).forEach((l) => { if (isFinite(l.lng) && isFinite(l.lat)) pts.push([l.lng, l.lat]); });
  (cfg.routes || []).forEach((r) => (r.coords || []).forEach((c) => pts.push(c)));
  if (pts.length < 1) return null;
  let minLng = pts[0][0], minLat = pts[0][1], maxLng = pts[0][0], maxLat = pts[0][1];
  pts.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  });
  return [[minLng, minLat], [maxLng, maxLat]];
}
